import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { db, storage } from '../Firebase';
import { collection, addDoc, onSnapshot, deleteDoc, doc, updateDoc } from 'firebase/firestore';
import { ref, deleteObject } from 'firebase/storage';
import { Plus, Trash2, Edit2, Upload, X, Check, ImageIcon } from 'lucide-react';
import Swal from 'sweetalert2';
import { motion, AnimatePresence } from 'framer-motion';
import { fileToBase64 } from '../utils/fileHelper';

const BannerManager = () => {
  const [banners, setBanners] = useState([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    title: '',
    subtitle: '',
    buttonText: '',
    buttonUrl: '',
    image: null,
    imageUrl: ''
  });

  useEffect(() => {
    const unsub = onSnapshot(collection(db, 'banners'), (snapshot) => {
      setBanners(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));
    });
    return () => unsub();
  }, []);

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 800 * 1024) {
        Swal.fire('Error', 'Image size should not exceed 800KB. Please upload an image below 800KB.', 'error');
        return;
      }
      setFormData({ ...formData, image: file });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate that an image is supplied either as a new upload or an existing url
    if (!formData.image && !formData.imageUrl) {
      Swal.fire('Error', 'Please upload a banner image. The image field is mandatory.', 'error');
      return;
    }

    setLoading(true);
    try {
      let finalImageUrl = formData.imageUrl;

      if (formData.image) {
        finalImageUrl = await fileToBase64(formData.image);
      }

      const data = {
        title: formData.title || '',
        subtitle: formData.subtitle || '',
        buttonText: formData.buttonText || '',
        buttonUrl: formData.buttonUrl || '',
        imageUrl: finalImageUrl,
        updatedAt: new Date()
      };

      if (editingId) {
        await updateDoc(doc(db, 'banners', editingId), data);
        Swal.fire('Success', 'Banner updated successfully', 'success');
      } else {
        await addDoc(collection(db, 'banners'), { ...data, createdAt: new Date() });
        Swal.fire('Success', 'Banner added successfully', 'success');
      }

      resetForm();
    } catch (error) {
      console.error(error);
      Swal.fire('Error', 'Something went wrong', 'error');
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({ title: '', subtitle: '', buttonText: '', buttonUrl: '', image: null, imageUrl: '' });
    setEditingId(null);
    setIsModalOpen(false);
  };

  const handleDelete = async (banner) => {
    const result = await Swal.fire({
      title: 'Are you sure?',
      text: "You won't be able to revert this!",
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#d33',
      cancelButtonColor: '#0D47A1',
      confirmButtonText: 'Yes, delete it!'
    });

    if (result.isConfirmed) {
      try {
        await deleteDoc(doc(db, 'banners', banner.id));
        // Also delete from storage if it's a firebase storage url
        if (banner.imageUrl.includes('firebasestorage')) {
          const imageRef = ref(storage, banner.imageUrl);
          await deleteObject(imageRef);
        }
        Swal.fire('Deleted!', 'Banner has been deleted.', 'success');
      } catch (error) {
        console.error(error);
        Swal.fire('Error', 'Failed to delete banner', 'error');
      }
    }
  };

  const startEdit = (banner) => {
    setFormData({
      title: banner.title || '',
      subtitle: banner.subtitle || '',
      buttonText: banner.buttonText || '',
      buttonUrl: banner.buttonUrl || '',
      imageUrl: banner.imageUrl || '',
      image: null
    });
    setEditingId(banner.id);
    setIsModalOpen(true);
  };

  return (
    <div className="space-y-8">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-primary-dark">Banner Manager</h1>
          <p className="text-gray-500 font-medium">Control the visual identity of your homepage.</p>
        </div>
        <button
          onClick={() => {
            if (banners.length >= 1) {
              Swal.fire({
                title: 'Limit Reached',
                text: 'Only one banner image is allowed. Please delete or edit the existing banner first.',
                icon: 'warning',
                confirmButtonColor: '#0D47A1'
              });
              return;
            }
            resetForm();
            setIsModalOpen(true);
          }}
          className="flex items-center gap-2 bg-primary hover:bg-primary-dark text-white px-6 py-3 rounded-2xl shadow-lg shadow-primary/20 transition-all active:scale-95 font-bold"
        >
          <Plus className="w-5 h-5" />
          Add New Banner
        </button>
      </div>

      {/* Banner Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        <AnimatePresence>
          {banners.map((banner) => (
            <motion.div
              key={banner.id}
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              className="bg-white rounded-[2.5rem] shadow-sm border border-gray-100 overflow-hidden group"
            >
              <div className="relative h-64 overflow-hidden">
                <img src={banner.imageUrl} alt={banner.title || 'Banner'} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
                <div className="absolute bottom-6 left-6 right-6">
                  {banner.title && <h3 className="text-white text-xl font-bold line-clamp-1">{banner.title}</h3>}
                  {banner.subtitle && <p className="text-white/80 text-sm line-clamp-1">{banner.subtitle}</p>}
                </div>
                <div className="absolute top-4 right-4 flex items-center gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
                  <button
                    onClick={() => startEdit(banner)}
                    className="p-2 bg-white/90 backdrop-blur-sm text-primary hover:bg-primary hover:text-white rounded-xl shadow-lg transition-all"
                  >
                    <Edit2 className="w-5 h-5" />
                  </button>
                  <button
                    onClick={() => handleDelete(banner)}
                    className="p-2 bg-white/90 backdrop-blur-sm text-red-500 hover:bg-red-500 hover:text-white rounded-xl shadow-lg transition-all"
                  >
                    <Trash2 className="w-5 h-5" />
                  </button>
                </div>
              </div>
              <div className="p-6 flex items-center justify-between bg-gray-50/50">
                <div className="flex items-center gap-2">
                  <span className="text-[10px] font-bold text-gray-400 uppercase tracking-widest">Button Text:</span>
                  <span className="text-sm font-bold text-primary-dark">{banner.buttonText || '(None)'}</span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-[10px] font-bold text-gray-400 uppercase tracking-widest">URL:</span>
                  <span className="text-sm text-blue-500 underline truncate max-w-[150px]">{banner.buttonUrl || '(None)'}</span>
                </div>
              </div>
            </motion.div>
          ))}
        </AnimatePresence>
      </div>

      {/* Modal */}
      {isModalOpen && createPortal(
        <div className="fixed inset-0 bg-primary-dark/60 backdrop-blur-sm z-[60] flex items-center justify-center p-4">
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-white rounded-[2.5rem] shadow-2xl w-full max-w-2xl overflow-hidden"
          >
            <div className="p-8 border-b border-gray-100 flex items-center justify-between">
              <h2 className="text-2xl font-bold text-primary-dark">{editingId ? 'Edit Banner' : 'Create New Banner'}</h2>
              <button onClick={() => setIsModalOpen(false)} className="p-2 hover:bg-gray-100 rounded-xl transition-colors">
                <X className="w-6 h-6 text-gray-400" />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="p-8 space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-2">
                  <label className="text-sm font-bold text-gray-700 ml-1">Banner Title</label>
                  <input
                    type="text"
                    value={formData.title}
                    onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                    className="w-full px-5 py-3.5 bg-gray-50 border border-gray-200 rounded-2xl focus:ring-2 focus:ring-primary/20 focus:border-primary outline-none transition-all"
                    placeholder="Enter catchy title..."
                  />
                </div>
                <div className="space-y-2">
                  <label className="text-sm font-bold text-gray-700 ml-1">Subtitle</label>
                  <input
                    type="text"
                    value={formData.subtitle}
                    onChange={(e) => setFormData({ ...formData, subtitle: e.target.value })}
                    className="w-full px-5 py-3.5 bg-gray-50 border border-gray-200 rounded-2xl focus:ring-2 focus:ring-primary/20 focus:border-primary outline-none transition-all"
                    placeholder="Short description..."
                  />
                </div>
                <div className="space-y-2">
                  <label className="text-sm font-bold text-gray-700 ml-1">Button Text</label>
                  <input
                    type="text"
                    value={formData.buttonText}
                    onChange={(e) => setFormData({ ...formData, buttonText: e.target.value })}
                    className="w-full px-5 py-3.5 bg-gray-50 border border-gray-200 rounded-2xl focus:ring-2 focus:ring-primary/20 focus:border-primary outline-none transition-all"
                    placeholder="e.g. Apply Now"
                  />
                </div>
                <div className="space-y-2">
                  <label className="text-sm font-bold text-gray-700 ml-1">Button URL</label>
                  <input
                    type="text"
                    value={formData.buttonUrl}
                    onChange={(e) => setFormData({ ...formData, buttonUrl: e.target.value })}
                    className="w-full px-5 py-3.5 bg-gray-50 border border-gray-200 rounded-2xl focus:ring-2 focus:ring-primary/20 focus:border-primary outline-none transition-all"
                    placeholder="/loans or https://..."
                  />
                </div>
              </div>

              <div className="space-y-2">
                <label className="text-sm font-bold text-gray-700 ml-1">Banner Image (Max 800KB)</label>
                <div className="relative h-48 group">
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handleImageChange}
                    className="absolute inset-0 w-full h-full opacity-0 cursor-pointer z-10"
                  />
                  <div className="h-full border-2 border-dashed border-gray-200 rounded-3xl flex flex-col items-center justify-center gap-3 bg-gray-50 group-hover:bg-gray-100 group-hover:border-primary/30 transition-all overflow-hidden">
                    {formData.image || formData.imageUrl ? (
                      <div className="relative w-full h-full">
                        <img 
                          src={formData.image ? URL.createObjectURL(formData.image) : formData.imageUrl} 
                          className="w-full h-full object-cover" 
                          alt="Preview"
                        />
                        <div className="absolute inset-0 bg-black/40 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                          <Upload className="w-8 h-8 text-white" />
                          <p className="text-white font-bold ml-2">Change Image</p>
                        </div>
                      </div>
                    ) : (
                      <>
                        <div className="w-12 h-12 bg-primary/10 rounded-2xl flex items-center justify-center">
                          <ImageIcon className="w-6 h-6 text-primary" />
                        </div>
                        <p className="text-gray-500 font-medium">Click or Drag to upload banner</p>
                      </>
                    )}
                  </div>
                </div>
              </div>

              <div className="flex gap-4 pt-4">
                <button
                  type="button"
                  onClick={() => setIsModalOpen(false)}
                  className="flex-1 px-8 py-4 bg-gray-100 text-gray-600 font-bold rounded-2xl hover:bg-gray-200 transition-all active:scale-95"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={loading}
                  className="flex-[2] px-8 py-4 bg-primary text-white font-bold rounded-2xl hover:bg-primary-dark shadow-lg shadow-primary/20 transition-all active:scale-95 flex items-center justify-center gap-2"
                >
                  {loading ? (
                    <div className="w-6 h-6 border-3 border-white/30 border-t-white rounded-full animate-spin" />
                  ) : (
                    <>
                      <Check className="w-5 h-5" />
                      {editingId ? 'Update Banner' : 'Save Banner'}
                    </>
                  )}
                </button>
              </div>
            </form>
          </motion.div>
        </div>,
        document.body
      )}
    </div>
  );
};

export default BannerManager;
